import { useCallback } from 'react';
import { authStore$ } from '../stores/auth.store';
import { ErrorCodesEnum } from '../enums/error-codes.enum';
import type { ValidatePhoneType } from '../types/front.graphql';
import { useTranslation } from 'react-i18next';
import type { RequestValidatePhoneParams } from '../types';
import { useRequestValidatePhone as useRequestValidatePhoneRequest } from '../api/requests';
import { useReCaptcha } from '@kidslabs/module-core';

export const useRequestValidatePhone = () => {
    const { t } = useTranslation();
    const { requestValidatePhone: doRequestValidatePhone } = useRequestValidatePhoneRequest();
    const { retrieveCaptchaToken } = useReCaptcha();

    return useCallback(
        async (params: RequestValidatePhoneParams, validateType: ValidatePhoneType) => {
            try {
                authStore$.notificationChannel.set(params.otpChannel);
                const data = authStore$.initValidatePhoneData.get();
                if (!data?.phoneNumber) {
                    Promise.reject(new Error('Phone number is required'));
                }
                const phoneNumber = data?.phoneNumber ?? '';
                const notificationChannel = params.otpChannel;
                const captchaResponseToken = await retrieveCaptchaToken('otpLogin', error =>
                    Promise.reject(new Error(`Initial recaptcha is error with message ${error}`))
                );

                const validatePhoneResponse = await doRequestValidatePhone({
                    variables: {
                        phoneNumber,
                        validateType: validateType,
                        notificationChannel,
                        sign: '',
                        captchaResponseToken: captchaResponseToken,
                    },
                });

                if (validatePhoneResponse?.data?.requestValidatePhone.success) {
                    authStore$.requestValidatePhoneResponse.set(validatePhoneResponse.data.requestValidatePhone);
                    const currentTime = Math.floor(Date.now() / 1000);
                    authStore$.otpStartTime.set(currentTime);
                    return {
                        message: 'Request validate phone successfully',
                        errorCode: ErrorCodesEnum.SUCCESS,
                    };
                } else if (
                    validatePhoneResponse?.data?.requestValidatePhone.error_code === ErrorCodesEnum.PHONE_REQUESTED
                ) {
                    return {
                        message: t('Số điện thoại đã gửi yêu cầu xác thực'),
                        errorCode: ErrorCodesEnum.PHONE_REQUESTED,
                    };
                } else {
                    return {
                        message:
                            validatePhoneResponse?.data?.requestValidatePhone.message ??
                            'Failed to request validate phone to login',
                        errorCode: ErrorCodesEnum.INVALID_REQUEST,
                    };
                }
            } catch (error) {
                console.error('Failed to request validate phone:', error);
                return {
                    message: `Failed to request validate phone to login with error ${error}`,
                    errorCode: ErrorCodesEnum.INVALID_REQUEST,
                };
            }
        },
        [doRequestValidatePhone, t, retrieveCaptchaToken]
    );
};
